import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Beef, LayoutDashboard, MapPin, Trophy, LogOut, Plus, Crown, Menu, X, Map as MapIcon, User, MessageCircle, Shield, Archive } from "lucide-react";
import { useState } from "react";
import NotificationBell from "@/components/NotificationBell";
import SupportButton from "@/components/SupportButton";
import ShareButton from "@/components/ShareButton";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const nav = [
    { to: "/dashboard", label: "Painel", icon: LayoutDashboard },
    { to: "/shifts", label: "Diárias", icon: MapPin },
    { to: "/map", label: "Mapa", icon: MapIcon },
    { to: "/messages", label: "Mensagens", icon: MessageCircle },
    { to: "/history", label: "Histórico", icon: Archive },
    { to: "/ranking", label: "Ranking", icon: Trophy },
  ];
  if (user?.role === "comercio") nav.push({ to: "/premium", label: "Premium", icon: Crown });
  if (user?.role === "admin") nav.push({ to: "/admin", label: "Admin", icon: Shield });

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen relative z-10">
      <header className="cw-glass sticky top-0 z-40 border-b border-zinc-800/70">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <Link to="/dashboard" className="flex items-center gap-2 notranslate" translate="no" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-lg bg-red-600 flex items-center justify-center">
              <Beef className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight">CarnesWork</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map(({ to, label, icon: Icon }) => {
              const active = pathname.startsWith(to);
              return (
                <Link key={to} to={to}
                  data-testid={`nav-${label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active ? "text-red-500 bg-red-500/10" : "text-zinc-300 hover:text-white hover:bg-zinc-800/60"
                  }`}>
                  <Icon className="w-4 h-4" /> {label}
                </Link>
              );
            })}
            <Link to="/profile" data-testid="nav-profile"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                pathname.startsWith("/profile") ? "text-red-500 bg-red-500/10" : "text-zinc-300 hover:text-white hover:bg-zinc-800/60"
              }`}>
              <User className="w-4 h-4" /> Perfil
            </Link>
            {user?.role === "comercio" && (
              <Link to="/shifts/new" data-testid="nav-new-shift" className="ml-2 cw-btn-primary text-sm inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Nova Diária
              </Link>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <NotificationBell />
            <ShareButton variant="icon" testid="btn-share-topbar" />
            <div className="text-right text-xs">
              <div className="font-semibold text-white">{user?.name}</div>
              <div className="text-zinc-500 uppercase tracking-wider text-[10px]">
                {user?.role === "comercio" ? "Comércio" : user?.role === "acougueiro" ? `${user?.specialty || "Açougueiro"} disponível` : "Admin"}
              </div>
            </div>
            <button onClick={handleLogout} className="cw-btn-outline text-sm !py-2 !px-3" data-testid="btn-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <NotificationBell />
            <ShareButton variant="icon" testid="btn-share-topbar-mobile" />
            <button className="text-zinc-300" onClick={() => setOpen(!open)} data-testid="btn-menu-toggle" aria-label="Menu">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-zinc-800 bg-zinc-950 px-4 py-3 space-y-2">
            {nav.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-zinc-300 hover:bg-zinc-800">
                <Icon className="w-4 h-4" /> {label}
              </Link>
            ))}
            {user?.role === "comercio" && (
              <Link to="/shifts/new" onClick={() => setOpen(false)} className="cw-btn-primary text-sm inline-flex items-center gap-2 w-full justify-center">
                <Plus className="w-4 h-4" /> Nova Diária
              </Link>
            )}
            <button onClick={handleLogout} className="cw-btn-outline text-sm w-full">Sair</button>
          </div>
        )}
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
      <SupportButton />
    </div>
  );
};

export default Layout;
